import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import CustomRichText from '@/components/CustomRichText/CustomRichText';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InputText from '@/components/UI/InputText/InputText';
import TableListEditableNew, { TABLE_ROW_SELECTION } from '@/components/UI/TableListEditable/TableListEditableNew';
import { ASC_ORDER, ENGLISH, GERMAN } from '@/helpers/constants';
import useAppLanguage from '@/hooks/useAppLanguage';
import UseGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import notificationsService from '@/services/notificationsService';
import titleService from '@/services/titleService';
import { Email, Variables } from '@/types';
import styles from './Emails.module.scss';

const EMAIL_EMPTY_FIELDS: Email = {
  title: { EN: '', DE: '' },
  subject: { EN: '', DE: '' },
  body: { EN: '<p>Body EN</p>', DE: '<p>Body DE</p>' },
};

const FIELDS_TO_EDIT = ['title', 'subject', 'body'];

const COLUMNS_NAMES = {
  code: 'code',
  name: 'name',
};

const EmailEdition: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const { goTo } = UseGoTo();
  const [loading, setLoading] = useState(true);
  const lang = useAppLanguage();
  const [email, setEmail] = useState<Email>({ ...EMAIL_EMPTY_FIELDS, body: { EN: '', DE: '' } });
  const [isPopupOpened, setIsPopupOpened] = useState<string>('');
  const [variables, setVariables] = useState<Variables[]>([]);
  const [selectionVars, setSelectionVars] = useState<string[]>([]);
  const [selectionToAdd, setSelectionToAdd] = useState<{ [ENGLISH]?: string; [GERMAN]?: string }>({});
  const isNewEmail = !id;

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('menu.emails'));
  }, [t, lang]);

  const getEmailDetail = (id: string) => {
    setLoading(true);
    notificationsService.getEmailDetails(id)
      .then(setEmail)
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => { setLoading(false); });
  };

  const getVariables = (langVars: string) => {
    notificationsService.getEmailVariables()
      .then((variablesBack: Variables[]) => setVariables(variablesBack.map((variable) => ({
        ...variable,
        name: variable?.name?.[langVars] || variable?.name?.EN,
      }))))
      .catch((error) => addToast(error.message, 'ERROR'));
  };

  useEffect(() => {
    if (id) {
      getEmailDetail(id);
    } else {
      setEmail(EMAIL_EMPTY_FIELDS);
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPopupOpened) {
      setSelectionVars([]);
      getVariables(isPopupOpened);
    }
  }, [isPopupOpened]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(() => [
    { name: COLUMNS_NAMES.code, title: t('administration.email.variable') },
    { name: COLUMNS_NAMES.name, title: t('administration.email.variableDescription') },
  ], [t]);

  const saveTemplate = (email: Email) => {
    setLoading(true);

    if (!isNewEmail) {
      notificationsService.saveEmailDetails(id, email).then(() => {
        goTo('/administration/emails/templates');
        addToast(t('administration.emails.emailTemplateEdited', { emailName: email.title[lang] }), 'Success');
      })
        .catch((error) => addToast(error.message, 'ERROR'))
        .finally(() => setLoading(false));
    } else {
      notificationsService.createEmailTemplate(email).then(() => {
        goTo('/administration/emails/templates');
        addToast(t('administration.emails.newEmailTemplateCreated'), 'Success');
      })
        .catch((error) => addToast(error.message, 'ERROR'))
        .finally(() => setLoading(false));
    }
  };

  const canSave = useMemo(() => Object.values(email).every((field) => Object.values(field).every((value) => (value as string).trim() !== '')), [email]);

  const handleInputChange = (label: keyof Email, value: string, language?: string) => {
    let newValue: string | { [key: string]: string } = value;
    const property = (typeof email[label] === 'object' && email[label] ? email[label] : {});

    if (language) {
      newValue = { ...property, [language]: value };
    }

    setEmail({ ...email, [label]: newValue });
  };

  const inputText = (field: keyof Email, language: string) => {
    return (
      <InputText
        id={`email-edition-${String(field)}-${language}`}
        label={`${t(`editableFields.${String(field)}`)}-${language}`}
        formControlClass={styles.InputSize}
        handleChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleInputChange(field, ev.target.value, language)}
        value={email?.[field]?.[language] || ''}
        disabled={loading}
      />
    );
  };

  const customRichText = (field: keyof Email, language: string) => {
    return (
      <CustomRichText
        key={`${id || 'new'}-${field}-${language}`}
        language={language}
        handleChange={(newValue) => handleInputChange('body', newValue, language)}
        id={`${String(field)}-${language}`}
        handleOpenVariables={() => {
          setIsPopupOpened(language);
          setSelectionToAdd({ ...selectionToAdd, [language]: '' }); // Reset selection for the new language
        }}
        initialValue={email[field]?.[language] || ''}
        variableSelected={selectionToAdd[language] || ''}
        title={`${t(`editableFields.body`)}-${language}`}
      />
    );
  };

  const onSelectionAddVarChange = (selection: string[]) => {
    const lastSelected = selection.at(selection.length - 1);
    setSelectionVars(lastSelected ? [lastSelected] : []);
  };

  return (
    <>
      <AccordionPanel title={`${isNewEmail ? t('administration.emails.newEmail') : t('menu.editEmail')} `}>
        {!loading && FIELDS_TO_EDIT.map((field, index) => (
          <div className={styles.RowContainer} key={`${field}-${index}-form`}>
            {Object.keys(email?.[field as keyof Email] ?? {}).map((language) => (
              <div className={styles.HalfRow} key={`${language} - column`}>
                {(field === 'body')
                  ? (customRichText(field as keyof Email, language))
                  : (inputText(field as keyof Email, language))}
              </div>
            ))}
          </div>
        ))}
        <div className={`${styles.ButtonsTable} ${styles.BottomButtons}`}>
          <Button
            id="email-edit-back"
            onClick={() => goTo('/administration/emails/templates')}
            disabled={loading}
          >
            {t('dialog.back')}
          </Button>
          <Button
            id="email-edit-save"
            variant="contained"
            color="primary"
            onClick={() => saveTemplate(email)}
            disabled={loading || !canSave}
          >
            {t('dialog.save')}
          </Button>
        </div>
      </AccordionPanel>
      {(isPopupOpened !== '' && variables.length > 0) && (
        <Dialog open fullWidth maxWidth="md" onClose={() => setIsPopupOpened('')}>
          <DialogTitle>{t('dialog.addVariables')}</DialogTitle>
          <DialogContent>
            <TableListEditableNew
              columnId="code"
              hasCheckbox
              rowSelectionMode={TABLE_ROW_SELECTION.SINGLE}
              isFiltering
              list={variables}
              columns={columns}
              selection={selectionVars}
              onSelectionChange={(selectedRows) => onSelectionAddVarChange(selectedRows as string[])}
              loading={loading}
              columnSorting={[{ columnName: COLUMNS_NAMES.code, direction: ASC_ORDER }]}
            />
          </DialogContent>
          <div className={`${styles.ButtonsTable} ${styles.BottomButtons}`}>
            <Button id="email-vars-back" onClick={() => setIsPopupOpened('')} disabled={loading}>
              {t('dialog.back')}
            </Button>
            <Button
              id="email-vars-apply"
              variant="contained"
              color="primary"
              onClick={() => {
                setIsPopupOpened('');
                setSelectionToAdd({ ...selectionToAdd, [isPopupOpened]: selectionVars[0] || '' });
              }}
              disabled={loading || selectionVars.length === 0}
            >
              {t('dialog.setVariable')}
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default EmailEdition;
